import _extends from '@babel/runtime/helpers/extends';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import EnterpriseIcon, { ChevronLeftDoubleIcon, ChevronRightDoubleIcon } from '@enterprise-ui/icons';
import classnames from 'classnames';
import React__default, { useState, useEffect } from 'react';
import FocusTrap from '../../utils/FocusTrap.js';
import { LayoutContext } from '../Layout/index.js';
import Overlay from '../Overlay.js';
import SideNavFooter from './SideNavFooter.js';
import SideNavHeader from './SideNavHeader.js';
import MinimizeToggle from './SideNavMinimizeToggle.js';
import SideNavNavigation from './SideNavNavigation.js';
import SideNavNavigationGroup from './SideNavNavigationGroup.js';
import SideNavNavigationGroupContent from './SideNavNavigationGroupContent.js';
import SideNavNavigationItem from './SideNavNavigationItem.js';
import SideNavSkipLink from './SideNavSkipLink.js';

var _excluded = ["children", "className", "data-testid", "isVisible", "isMinimized", "hasOverlay", "onRequestClose", "suppressAutoOpen", "canMinimize", "onMinimizeChange", "onMinimizeToggle"];
var SideNavContext = /*#__PURE__*/React__default.createContext();
SideNavContext.displayName = 'SideNavContext';
var SideNavGroupContext = /*#__PURE__*/React__default.createContext();
SideNavGroupContext.displayName = 'SideNavGroupContext';
var SideNav = function SideNav(_ref) {
  var children = _ref.children,
    className = _ref.className,
    dataTestId = _ref['data-testid'],
    _ref$isVisible = _ref.isVisible,
    propsIsVisible = _ref$isVisible === void 0 ? true : _ref$isVisible,
    _ref$isMinimized = _ref.isMinimized,
    propsIsMinimized = _ref$isMinimized === void 0 ? false : _ref$isMinimized,
    hasOverlay = _ref.hasOverlay,
    propsOnRequestClose = _ref.onRequestClose,
    _ref$suppressAutoOpen = _ref.suppressAutoOpen,
    suppressAutoOpen = _ref$suppressAutoOpen === void 0 ? false : _ref$suppressAutoOpen,
    _ref$canMinimize = _ref.canMinimize,
    canMinimize = _ref$canMinimize === void 0 ? false : _ref$canMinimize,
    onMinimizeChange = _ref.onMinimizeChange;
    _ref.onMinimizeToggle;
    var otherProps = _objectWithoutProperties(_ref, _excluded);
  var _React$useContext = React__default.useContext(LayoutContext),
    setIsLayoutExpanded = _React$useContext.setIsLayoutExpanded;
  var _useState = useState(propsIsVisible),
    _useState2 = _slicedToArray(_useState, 2),
    savedIsVisible = _useState2[0],
    setSavedIsVisible = _useState2[1];
  var _useState3 = useState(propsIsVisible),
    _useState4 = _slicedToArray(_useState3, 2),
    isSideNavVisible = _useState4[0],
    setIsSideNavVisible = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    isSideNavDomHidden = _useState6[0],
    setIsSideNavDomHidden = _useState6[1];
  var _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    isMinimized = _useState8[0],
    setIsMinimized = _useState8[1];
  var _useState9 = useState(typeof window !== 'undefined' && window.innerWidth),
    _useState10 = _slicedToArray(_useState9, 2),
    windowWidth = _useState10[0],
    setWindowWidth = _useState10[1];
  var canvasLgBreakpoint = 1200;
  useEffect(function () {
    setIsMinimized(propsIsMinimized);
  }, [propsIsMinimized]);
  useEffect(function () {
    if (typeof setIsLayoutExpanded === 'function') {
      setIsLayoutExpanded(isMinimized);
    }
  }, [isMinimized]);
  var handleToggleSideNavVisibility = function handleToggleSideNavVisibility(newVisibilityState) {
    if (newVisibilityState === true) {
      // set this to visible in the DOM
      setIsSideNavDomHidden(false);
      // give it .1 seconds to attach the transition to it
      setTimeout(function () {
        // then add the visibility class so it animates open
        setIsSideNavVisible(true);
      }, 100);
    } else {
      // remove the visibility class so that it animates out
      setIsSideNavVisible(false);
      // wait for the duration of the animation
      setTimeout(function () {
        // then remove it from the DOM so screen readers do not tab through it
        setIsSideNavDomHidden(true);
      }, 300);
    }
  };
  useEffect(function () {
    var updateWindowWidth = function updateWindowWidth() {
      setWindowWidth(window.innerWidth);
      if (windowWidth > canvasLgBreakpoint && !suppressAutoOpen) {
        handleToggleSideNavVisibility(true);
      }
      if (windowWidth < canvasLgBreakpoint && !suppressAutoOpen) {
        handleToggleSideNavVisibility(savedIsVisible);
      }
    };
    window.addEventListener('resize', updateWindowWidth);
    updateWindowWidth();
    return function () {
      return window.removeEventListener('resize', updateWindowWidth);
    };
  }, [savedIsVisible, windowWidth]);
  useEffect(function () {
    setSavedIsVisible(propsIsVisible);
    handleToggleSideNavVisibility(propsIsVisible);
  }, [propsIsVisible]);
  var onRequestClose = function onRequestClose() {
    propsOnRequestClose && propsOnRequestClose();
  };
  var finalClasses = classnames('C-SideNav', {
    '--suppressAutoOpen': suppressAutoOpen && !isSideNavVisible,
    isMinimized: isMinimized,
    isVisible: isSideNavVisible
  }, className);
  var finalOverlayClasses = classnames('C-Overlay--SideNav', {
    isMinimized: isMinimized,
    isUndefined: hasOverlay === undefined && isSideNavVisible,
    isVisible: (hasOverlay === undefined || hasOverlay) && isSideNavVisible
  }, className);
  var shouldTrapFocus = function shouldTrapFocus() {
    var isForcedOpenAndVisible = suppressAutoOpen === true && isSideNavVisible;
    var hasOverlayOrUnset = hasOverlay === true | hasOverlay === undefined;
    if (
    // if it is forced open as a modal-style nav at all widths and it is visible
    // trap focus, unless there is explicitly no overlay
    isForcedOpenAndVisible && hasOverlayOrUnset && !isMinimized ||
    // if the window width is under 1200 (in css forcing a modal-style side nav)
    // trap focus, unless there is explicitly no overlay
    windowWidth < 1200 && isSideNavVisible === true && !isMinimized && hasOverlayOrUnset) {
      return true;
    } else {
      return false;
    }
  };
  var handleToggleMinimization = function handleToggleMinimization(e) {
    e.stopPropagation();
    setIsMinimized(!isMinimized);
    onMinimizeChange && onMinimizeChange(!isMinimized);
  };
  var handleAutoClose = function handleAutoClose() {
    onRequestClose && onRequestClose();
  };
  return /*#__PURE__*/React__default.createElement(SideNavContext.Provider, {
    value: {
      handleAutoClose: handleAutoClose,
      isMinimized: isMinimized
    }
  }, /*#__PURE__*/React__default.createElement(FocusTrap, _extends({
    as: "nav",
    contentClassName: finalClasses,
    active: shouldTrapFocus(),
    onEscape: onRequestClose,
    "data-testid": dataTestId,
    "aria-expanded": !isMinimized,
    "aria-hidden": isSideNavDomHidden,
    hidden: isSideNavDomHidden,
    "aria-label": "Side Navigation"
  }, otherProps), children, canMinimize && /*#__PURE__*/React__default.createElement(MinimizeToggle, {
    "data-testid": dataTestId ? "".concat(dataTestId, "_toggle") : null,
    onClick: handleToggleMinimization,
    "aria-label": isMinimized ? 'Expand side navigation' : 'Minimize side navigation'
  }, /*#__PURE__*/React__default.createElement(EnterpriseIcon, {
    icon: isMinimized ? ChevronLeftDoubleIcon : ChevronRightDoubleIcon
  }))), /*#__PURE__*/React__default.createElement(Overlay, {
    "data-testid": dataTestId ? "".concat(dataTestId, "_overlay") : null,
    className: finalOverlayClasses,
    type: "subtle",
    onClick: onRequestClose
  }));
};
SideNav.displayName = 'SideNav';
SideNav.Footer = SideNavFooter;
SideNav.Header = SideNavHeader;
SideNav.SkipLink = SideNavSkipLink;
SideNav.Navigation = SideNavNavigation;
SideNav.MinimizeToggle = MinimizeToggle;
SideNav.NavigationItem = SideNavNavigationItem;
SideNav.NavigationGroup = SideNavNavigationGroup;
SideNav.NavigationGroupContent = SideNavNavigationGroupContent;

export { SideNavContext, SideNavGroupContext, SideNav as default };
